import {
  collection,
  doc,
  getDocs,
  addDoc,
  updateDoc,
  query,
  where,
  limit,
  onSnapshot,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { db } from './firebaseService';
import type {
  Subscription,
  SubscriptionPlan,
  SubscriptionCheckResult,
  SubscriptionUsage,
  SubscriptionLimits,
  SubscriptionChangeRequest,
  PaymentInfo,
  SubscriptionNotificationSettings,
  SubscriptionStats,
  SubscriptionType,
  SubscriptionStatus,
  PaymentMethod
} from '../types/subscription';

// 구독 플랜 데이터 (실제로는 Firestore에서 관리)
export const SUBSCRIPTION_PLANS: SubscriptionPlan[] = [
  {
    id: 'free',
    name: '무료 플랜',
    type: 'FREE',
    price: 0,
    currency: 'KRW',
    billingCycle: 'MONTHLY',
    features: [
      '기본 가계부 관리',
      '최대 1개 매장',
      '최대 10개 거래 내역/월',
      '기본 리포트',
    ],
    maxStores: 1,
    maxEmployees: 0,
    maxExpenses: 10,
  },
  {
    id: 'basic',
    name: '베이직 플랜',
    type: 'BASIC',
    price: 9900,
    currency: 'KRW',
    billingCycle: 'MONTHLY',
    features: [
      '무제한 가계부 관리',
      '최대 3개 매장',
      '무제한 거래 내역',
      '고급 리포트',
      '직원 관리 (최대 2명)',
      '이메일 지원',
    ],
    maxStores: 3,
    maxEmployees: 2,
    maxExpenses: -1, // 무제한
  },
  {
    id: 'premium',
    name: '프리미엄 플랜',
    type: 'PREMIUM',
    price: 19900,
    currency: 'KRW',
    billingCycle: 'MONTHLY',
    features: [
      '모든 베이직 기능',
      '최대 10개 매장',
      '무제한 직원 관리',
      '고급 분석 도구',
      'API 접근',
      '우선 지원',
      '커스텀 리포트',
    ],
    maxStores: 10,
    maxEmployees: -1, // 무제한
    maxExpenses: -1,
    isPopular: true,
  },
  {
    id: 'enterprise',
    name: '엔터프라이즈 플랜',
    type: 'ENTERPRISE',
    price: 49900,
    currency: 'KRW',
    billingCycle: 'MONTHLY',
    features: [
      '모든 프리미엄 기능',
      '무제한 매장',
      '전담 계정 매니저',
      '커스텀 통합',
      'SSO 지원',
      '24/7 전화 지원',
      '데이터 백업',
    ],
    maxStores: -1, // 무제한
    maxEmployees: -1,
    maxExpenses: -1,
  },
];

// 구독 컬렉션 참조 (사용자별 하위 컬렉션)
const getSubscriptionCollection = (userId: string) => 
  collection(db, 'users', userId, 'subscriptions');

const getSubscriptionDoc = (userId: string, subscriptionId: string) =>
  doc(db, 'users', userId, 'subscriptions', subscriptionId);

export const normalizeSubscriptionType = (type?: string | null): string =>
  String(type || '').toUpperCase().trim();

export const isPremiumSubscriptionType = (type?: string | null): boolean => {
  const normalizedType = normalizeSubscriptionType(type);
  return normalizedType === 'PREMIUM' || normalizedType === 'SERVICE_PREMIUM';
};

export const hasWebSubscriptionType = (type?: string | null): boolean => {
  const normalizedType = normalizeSubscriptionType(type);
  return isPremiumSubscriptionType(normalizedType) || normalizedType === 'ENTERPRISE';
};

// Timestamp를 Date로 안전하게 변환하는 헬퍼 함수
const toDateSafe = (value: any): Date | undefined => {
  if (!value) return undefined;
  if (value instanceof Date) return value;
  if (value && typeof value.toDate === 'function') {
    return value.toDate();
  }
  // 문자열이나 숫자인 경우
  if (typeof value === 'string' || typeof value === 'number') {
    const date = new Date(value);
    return isNaN(date.getTime()) ? undefined : date;
  }
  return undefined;
};

// 사용자의 현재 구독 정보 가져오기
export const getCurrentSubscription = async (userId: string): Promise<Subscription | null> => {
  try {
    
    const subscriptionsRef = getSubscriptionCollection(userId);
    
    // 사용자별 하위 컬렉션이므로 where 절 없이 직접 조회
    const q = query(
      subscriptionsRef,
      limit(10) // 최대 10개까지 가져와서 클라이언트에서 정렬
    );
    
    const snapshot = await getDocs(q);
    
    if (snapshot.empty) {
      return null;
    }
    
    // 클라이언트에서 정렬 (최신순)
    const sortedDocs = snapshot.docs.sort((a, b) => {
      const aTime = toDateSafe(a.data().createdAt) || new Date(0);
      const bTime = toDateSafe(b.data().createdAt) || new Date(0);
      return bTime.getTime() - aTime.getTime();
    });
    
    const doc = sortedDocs[0];
    const data = doc.data();
    
    
    return {
      id: doc.id,
      ...data,
      startDate: toDateSafe(data.startDate) || new Date(),
      endDate: toDateSafe(data.endDate) || new Date(),
      lastPaymentDate: toDateSafe(data.lastPaymentDate),
      nextPaymentDate: toDateSafe(data.nextPaymentDate),
      createdAt: toDateSafe(data.createdAt) || new Date(),
      updatedAt: toDateSafe(data.updatedAt) || new Date(),
    } as Subscription;
  } catch (error) {
    console.error('❌ 구독 정보 조회 실패:', error);
    console.error('❌ 에러 상세:', error);
    throw error;
  }
};

// 구독 상태 체크
export const checkSubscriptionStatus = async (userId: string): Promise<SubscriptionCheckResult> => {
  try {
    const subscription = await getCurrentSubscription(userId);
    
    if (!subscription) {
      return {
        hasAccess: false,
        hasWebAccess: false,
        hasPremiumAccess: false,
        subscriptionType: 'FREE',
        isExpired: true,
        isNearExpiry: false,
        daysUntilExpiry: 0,
        canUpgrade: true,
        canDowngrade: false,
        usagePercentage: { stores: 0, employees: 0, expenses: 0 },
      };
    }
    
    const now = new Date();
    // endDate가 null인 경우 만료되지 않은 것으로 처리
    const isExpired = subscription.endDate ? subscription.endDate < now : false;
    const daysUntilExpiry = subscription.endDate ? 
      Math.ceil((subscription.endDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)) : 
      -1; // 무제한
    const isNearExpiry = daysUntilExpiry <= 7 && daysUntilExpiry > 0;
    
    // 기본 사용량 정보 (실제 데이터에 없을 수 있음)
    const usagePercentage = {
      stores: subscription.limits?.maxStores === -1 ? 0 : ((subscription.usage?.currentStores || 0) / (subscription.limits?.maxStores || 1)) * 100,
      employees: subscription.limits?.maxEmployees === -1 ? 0 : ((subscription.usage?.currentEmployees || 0) / (subscription.limits?.maxEmployees || 1)) * 100,
      expenses: subscription.limits?.maxExpenses === -1 ? 0 : ((subscription.usage?.currentExpenses || 0) / (subscription.limits?.maxExpenses || 1)) * 100,
    };
    
    // 구독 상태 확인 (status 필드가 없으면 ACTIVE로 간주)
    const status = subscription.status || 'ACTIVE';
    const hasAccess = !isExpired && status === 'ACTIVE';
    
    // 구독 타입 정규화 (대소문자 구분 없이)
    const normalizedType = normalizeSubscriptionType(subscription.type);
    
    // SERVICE_PREMIUM도 PREMIUM과 동일한 프리미엄 접근으로 허용
    const hasWebAccess = hasAccess && hasWebSubscriptionType(normalizedType);
    const hasPremiumAccess = hasAccess && isPremiumSubscriptionType(normalizedType);
    
    
    return {
      hasAccess,
      hasWebAccess,
      hasPremiumAccess,
      subscriptionType: subscription.type as any,
      subscription,
      isExpired,
      isNearExpiry,
      daysUntilExpiry,
      canUpgrade: subscription.type !== 'ENTERPRISE',
      canDowngrade: subscription.type !== 'FREE',
      usagePercentage,
    };
  } catch (error) {
    console.error('구독 상태 체크 실패:', error);
    throw error;
  }
};

// 새 구독 생성
export const createSubscription = async (
  userId: string,
  planId: string,
  paymentMethod: string = 'CREDIT_CARD'
): Promise<string> => {
  try {
    const plan = SUBSCRIPTION_PLANS.find(p => p.id === planId);
    if (!plan) {
      throw new Error('유효하지 않은 플랜입니다.');
    }
    
    const now = new Date();
    const endDate = new Date();
    endDate.setMonth(endDate.getMonth() + (plan.billingCycle === 'YEARLY' ? 12 : 1));
    
    const subscriptionData = {
      userId,
      planId: plan.id,
      type: plan.type,
      status: 'ACTIVE' as SubscriptionStatus,
      startDate: Timestamp.fromDate(now),
      endDate: Timestamp.fromDate(endDate),
      autoRenew: true,
      paymentMethod,
      amount: plan.price,
      currency: plan.currency,
      billingCycle: plan.billingCycle,
      features: plan.features,
      limits: {
        maxStores: plan.maxStores,
        maxEmployees: plan.maxEmployees,
        maxExpenses: plan.maxExpenses,
      },
      usage: {
        currentStores: 0,
        currentEmployees: 0,
        currentExpenses: 0,
      },
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };
    
    const docRef = await addDoc(getSubscriptionCollection(userId), subscriptionData);
    return docRef.id;
  } catch (error) {
    console.error('구독 생성 실패:', error);
    throw error;
  }
};

// 구독 업그레이드/다운그레이드
export const changeSubscription = async (
  userId: string,
  subscriptionId: string,
  newPlanId: string
): Promise<void> => {
  try {
    const plan = SUBSCRIPTION_PLANS.find(p => p.id === newPlanId);
    if (!plan) {
      throw new Error('유효하지 않은 플랜입니다.');
    }
    
    const subscriptionRef = getSubscriptionDoc(userId, subscriptionId);
    const endDate = new Date();
    endDate.setMonth(endDate.getMonth() + (plan.billingCycle === 'YEARLY' ? 12 : 1));
    
    await updateDoc(subscriptionRef, {
      planId: plan.id,
      type: plan.type,
      amount: plan.price,
      features: plan.features,
      limits: {
        maxStores: plan.maxStores,
        maxEmployees: plan.maxEmployees,
        maxExpenses: plan.maxExpenses,
      },
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('구독 변경 실패:', error);
    throw error;
  }
};

// 구독 취소
export const cancelSubscription = async (userId: string, subscriptionId: string): Promise<void> => {
  try {
    const subscriptionRef = getSubscriptionDoc(userId, subscriptionId);
    await updateDoc(subscriptionRef, {
      status: 'CANCELLED',
      autoRenew: false,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('구독 취소 실패:', error);
    throw error;
  }
};

// 구독 사용량 업데이트
export const updateSubscriptionUsage = async (
  userId: string,
  subscriptionId: string,
  usage: Partial<SubscriptionUsage>
): Promise<void> => {
  try {
    const subscriptionRef = getSubscriptionDoc(userId, subscriptionId);
    await updateDoc(subscriptionRef, {
      'usage.currentStores': usage.stores,
      'usage.currentEmployees': usage.employees,
      'usage.currentExpenses': usage.expenses,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('구독 사용량 업데이트 실패:', error);
    throw error;
  }
};

// 구독 실시간 리스너
export const onSubscriptionSnapshot = (
  userId: string,
  callback: (subscription: Subscription | null) => void
): (() => void) => {
  const subscriptionsRef = getSubscriptionCollection(userId);
  // 사용자별 하위 컬렉션이므로 where 절 없이 직접 조회
  const q = query(
    subscriptionsRef,
    limit(10)
  );
  
  return onSnapshot(q, (snapshot) => {
    if (snapshot.empty) {
      callback(null);
      return;
    }
    
    // 클라이언트에서 정렬 (최신순)
    const sortedDocs = snapshot.docs.sort((a, b) => {
      const aTime = toDateSafe(a.data().createdAt) || new Date(0);
      const bTime = toDateSafe(b.data().createdAt) || new Date(0);
      return bTime.getTime() - aTime.getTime();
    });
    
    const doc = sortedDocs[0];
    const data = doc.data();
    
    const subscription: Subscription = {
      id: doc.id,
      ...data,
      startDate: toDateSafe(data.startDate) || new Date(),
      endDate: toDateSafe(data.endDate) || new Date(),
      lastPaymentDate: toDateSafe(data.lastPaymentDate),
      nextPaymentDate: toDateSafe(data.nextPaymentDate),
      createdAt: toDateSafe(data.createdAt) || new Date(),
      updatedAt: toDateSafe(data.updatedAt) || new Date(),
    } as Subscription;
    
    callback(subscription);
  });
};

// 구독 플랜 목록 가져오기
export const getSubscriptionPlans = (): SubscriptionPlan[] => {
  return SUBSCRIPTION_PLANS;
};

// 특정 플랜 정보 가져오기
export const getSubscriptionPlan = (planId: string): SubscriptionPlan | null => {
  return SUBSCRIPTION_PLANS.find(plan => plan.id === planId) || null;
};

// 웹 접근 권한 체크
export const checkWebAccess = async (userId: string): Promise<boolean> => {
  try {
    const result = await checkSubscriptionStatus(userId);
    return result.hasAccess && hasWebSubscriptionType(result.subscription?.type);
  } catch (error) {
    console.error('웹 접근 권한 체크 실패:', error);
    return false;
  }
};

// 프리미엄 기능 접근 권한 체크
export const checkPremiumAccess = async (userId: string): Promise<boolean> => {
  try {
    const result = await checkSubscriptionStatus(userId);
    return result.hasAccess && isPremiumSubscriptionType(result.subscription?.type);
  } catch (error) {
    console.error('프리미엄 접근 권한 체크 실패:', error);
    return false;
  }
};

// 타입들 re-export
export type {
  Subscription,
  SubscriptionPlan,
  SubscriptionCheckResult,
  SubscriptionUsage,
  SubscriptionLimits,
  SubscriptionChangeRequest,
  PaymentInfo,
  SubscriptionNotificationSettings,
  SubscriptionStats,
  SubscriptionType,
  SubscriptionStatus,
  PaymentMethod
};
